import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Calendar as CalendarIcon,
  Clock,
  RefreshCw,
  Loader2,
  Settings,
  Trash2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const CalendarsPage = () => {
  const { locationId } = useParams();
  const { isAdmin } = useAuth();
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(null); // Track individual calendar sync
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [deleteCalendar, setDeleteCalendar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchCalendars = async () => {
    try {
      const response = await axios.get(`${API}/locations/${locationId}/calendars`);
      setCalendars(response.data);
    } catch (error) {
      console.error('Failed to fetch calendars:', error);
      toast.error('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendars();
  }, [locationId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/locations/${locationId}/sync-calendars`);
      toast.success(response.data.message);
      fetchCalendars();
    } catch (error) {
      toast.error('Failed to sync calendars');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSingleCalendar = async (calendarId, calendarName) => {
    setSyncingCalendar(calendarId);
    try {
      const response = await axios.post(`${API}/calendars/${calendarId}/sync`);
      toast.success(response.data.message);
      fetchCalendars();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to sync ${calendarName}`);
    } finally {
      setSyncingCalendar(null);
    }
  };

  const handleSave = async () => {
    if (!editingCalendar) return;
    setSaving(true);
    
    try {
      await axios.put(`${API}/calendars/${editingCalendar.id}`, {
        slot_duration: editingCalendar.slot_duration,
        slot_interval: editingCalendar.slot_interval,
        working_hours: editingCalendar.working_hours
      });
      
      toast.success('Calendar updated successfully');
      setEditingCalendar(null);
      fetchCalendars();
    } catch (error) {
      toast.error('Failed to update calendar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCalendar) return;
    setDeleting(true);
    
    try {
      await axios.delete(`${API}/calendars/${deleteCalendar.id}`);
      toast.success('Calendar deleted successfully');
      setDeleteCalendar(null);
      fetchCalendars();
    } catch (error) {
      toast.error('Failed to delete calendar');
    } finally {
      setDeleting(false);
    }
  };

  const updateWorkingHours = (day, field, value) => {
    if (!editingCalendar) return;
    
    setEditingCalendar(prev => ({
      ...prev,
      working_hours: prev.working_hours.map(h => 
        h.day === day ? { ...h, [field]: value } : h
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendars-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/locations" className="hover:text-slate-700">Locations</Link>
        <span>/</span>
        <Link to={`/locations/${locationId}`} className="hover:text-slate-700">Location</Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">Calendars</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Calendars
          </h1>
          <p className="text-slate-500 mt-1">
            Manage calendar availability and working hours
          </p>
        </div>
        {isAdmin() && (
          <Button 
            variant="outline" 
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-calendars-btn"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync from CRM
          </Button>
        )}
      </div>

      {/* Calendars List */}
      {calendars.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No calendars synced
            </h3>
            <p className="text-slate-500 mb-6">
              Click &quot;Sync from CRM&quot; to pull calendars from your location
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {calendars.map((calendar) => (
            <Card key={calendar.id} className="overflow-hidden" data-testid={`calendar-card-${calendar.id}`}>
              <CardHeader className="pb-3 bg-gradient-to-r from-slate-50 to-slate-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <CalendarIcon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {calendar.calendar_name}
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {calendar.slot_duration} min slots • {calendar.timezone || 'Local'}
                      </p>
                    </div>
                  </div>
                  {/* Sync Status Indicator */}
                  <div className="flex items-center gap-2">
                    {calendar.sync_status === 'error' ? (
                      <span className="text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded" title={calendar.sync_error}>
                        Sync Error
                      </span>
                    ) : calendar.last_sync ? (
                      <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                        Synced
                      </span>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Working Hours - Combined View */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Availability</p>
                  <div className="grid grid-cols-7 gap-1">
                    {calendar.working_hours?.map((h) => (
                      <div 
                        key={h.day}
                        className={`text-center p-2 rounded text-xs ${
                          h.enabled 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : 'bg-slate-50 text-slate-400 border border-slate-100'
                        }`}
                        title={h.enabled ? `${h.start_time} - ${h.end_time}` : 'Unavailable'}
                      >
                        <div className="font-medium">{h.day.slice(0, 2).toUpperCase()}</div>
                        {h.enabled && (
                          <div className="text-[10px] mt-0.5">{h.start_time}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Team Members */}
                {calendar.team_members && calendar.team_members.length > 0 && (
                  <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                    Team: {calendar.team_members.map(tm => tm.name || tm.firstName || 'User').join(', ')}
                  </div>
                )}

                {/* Last Sync Time */}
                {calendar.last_sync && (
                  <div className="text-xs text-slate-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Last sync: {new Date(calendar.last_sync).toLocaleString()}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleSyncSingleCalendar(calendar.id, calendar.calendar_name)}
                    disabled={syncingCalendar === calendar.id}
                    data-testid={`sync-calendar-${calendar.id}`}
                  >
                    {syncingCalendar === calendar.id ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    Sync
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => setEditingCalendar(calendar)}
                    data-testid={`edit-calendar-${calendar.id}`}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Edit Hours
                  </Button>
                  {isAdmin() && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => setDeleteCalendar(calendar)}
                      data-testid={`delete-calendar-${calendar.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingCalendar} onOpenChange={() => setEditingCalendar(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Calendar: {editingCalendar?.calendar_name}
            </DialogTitle>
            <DialogDescription>
              Configure slot duration and working hours
            </DialogDescription>
          </DialogHeader>

          {editingCalendar && (
            <div className="space-y-6 py-4">
              {/* Slot Configuration */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Slot Duration (min)</Label>
                  <Select
                    value={String(editingCalendar.slot_duration)}
                    onValueChange={(val) => setEditingCalendar(prev => ({ ...prev, slot_duration: parseInt(val) }))}
                  >
                    <SelectTrigger data-testid="slot-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60, 90, 120].map(d => (
                        <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Slot Interval (min)</Label>
                  <Select
                    value={String(editingCalendar.slot_interval)}
                    onValueChange={(val) => setEditingCalendar(prev => ({ ...prev, slot_interval: parseInt(val) }))}
                  >
                    <SelectTrigger data-testid="slot-interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60].map(d => (
                        <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Working Hours */}
              <div className="space-y-3">
                <Label>Working Hours</Label>
                <div className="working-hours-grid">
                  {editingCalendar.working_hours?.map((h) => (
                    <div key={h.day} className="working-hours-row">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={h.enabled}
                          onCheckedChange={(checked) => updateWorkingHours(h.day, 'enabled', checked)}
                          data-testid={`toggle-${h.day}`}
                        />
                        <span className="font-medium capitalize text-sm w-20">{h.day}</span>
                      </div>
                      <Input
                        type="time"
                        value={h.start_time}
                        onChange={(e) => updateWorkingHours(h.day, 'start_time', e.target.value)}
                        disabled={!h.enabled}
                        className="text-sm"
                        data-testid={`start-time-${h.day}`}
                      />
                      <Input
                        type="time"
                        value={h.end_time}
                        onChange={(e) => updateWorkingHours(h.day, 'end_time', e.target.value)}
                        disabled={!h.enabled}
                        className="text-sm"
                        data-testid={`end-time-${h.day}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCalendar(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-calendar-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCalendar} onOpenChange={() => setDeleteCalendar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Calendar</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteCalendar?.calendar_name}&quot;? This will also delete the calendar from the CRM. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-calendar-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CalendarsPage;
